/**
 * SiteFeaturesPage Component
 *
 * The Features tab: a switch per site feature flag, saved together.
 */

import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useSiteFeatures } from '../hooks/useSiteFeatures';
import { SITE_FEATURE_FLAGS, SITE_FEATURE_INFO } from '../models/domainModels';
import { FeatureToggle } from './FeatureToggle';
import './SiteFeaturesPage.css';

const TITLE = 'Site Features';

/** Absolute local date and time; flag changes are rare, so relative time is vaguer than useful. */
function formatUpdatedAt(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

function Notice({
  role,
  children,
}: {
  readonly role: 'alert' | 'status';
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <div className="site-features-page">
      <div className="site-features-page__notice" role={role}>
        <h1 className="site-features-page__title">{TITLE}</h1>
        {children}
      </div>
    </div>
  );
}

export function SiteFeaturesPage(): JSX.Element {
  const { siteId } = useParams<{ siteId: string }>();
  const {
    stored,
    features,
    changedFlags,
    hasUnsavedChanges,
    isLoading,
    isSaving,
    isNotFound,
    error,
    authError,
    setFeature,
    save,
    discard,
    reload,
  } = useSiteFeatures(siteId);

  if (isLoading) {
    return (
      <div className="site-features-page">
        <p className="site-features-page__muted">Loading features…</p>
      </div>
    );
  }

  if (authError === 'forbidden') {
    return (
      <Notice role="alert">
        <p>
          Your account does not have permission to manage site features. Signing in
          again will not change this. Ask the Toptracer Go team for the required access.
        </p>
      </Notice>
    );
  }

  if (authError === 'expired') {
    return (
      <Notice role="alert">
        <p>Your session has expired. Sign in again to continue.</p>
      </Notice>
    );
  }

  if (isNotFound) {
    return (
      <Notice role="status">
        <p>
          This site has no Toptracer Go configuration yet, so its features cannot be
          set.
        </p>
      </Notice>
    );
  }

  if (stored === null) {
    return (
      <div className="site-features-page">
        <h1 className="site-features-page__title">{TITLE}</h1>
        <p className="site-features-page__error" role="alert">
          {error ?? 'Features could not be loaded.'}
        </p>
        <div>
          <button
            type="button"
            className="site-features-page__button"
            onClick={() => void reload()}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="site-features-page">
      <h1 className="site-features-page__title">{TITLE}</h1>

      {error !== null && (
        <p className="site-features-page__error" role="alert">
          {error}
        </p>
      )}

      <section className="site-features-page__panel" aria-labelledby="site-features-flags">
        <h2 className="site-features-page__panel-title" id="site-features-flags">
          Player features
        </h2>
        <p className="site-features-page__muted">
          Changes apply to players the next time they load this site.
        </p>

        <div className="site-features-page__list">
          {SITE_FEATURE_FLAGS.map((flag) => (
            <FeatureToggle
              key={flag}
              label={SITE_FEATURE_INFO[flag].label}
              description={SITE_FEATURE_INFO[flag].description}
              checked={features[flag]}
              isChanged={changedFlags.includes(flag)}
              isBeta={SITE_FEATURE_INFO[flag].isBeta}
              disabled={isSaving}
              onChange={(enabled) => setFeature(flag, enabled)}
            />
          ))}
        </div>

        <div className="site-features-page__footer">
          <span className="site-features-page__muted">
            {stored.updatedAt !== null
              ? `Last changed ${formatUpdatedAt(stored.updatedAt)}`
              : 'These features have never been changed for this site.'}
          </span>
          <div className="site-features-page__actions">
            <button
              type="button"
              className="site-features-page__button site-features-page__button--quiet"
              onClick={discard}
              disabled={!hasUnsavedChanges || isSaving}
            >
              Discard
            </button>
            <button
              type="button"
              className="site-features-page__button site-features-page__button--primary"
              onClick={() => void save()}
              disabled={!hasUnsavedChanges || isSaving}
            >
              {isSaving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
